import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { metricTile, type MetricTileTone } from '@/lib/metricTileStyles';

interface MetricTileProps {
  tone: MetricTileTone;
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  badge?: string;
  className?: string;
  /** Slightly smaller value text for long strings (dates, phases). */
  compactValue?: boolean;
}

/** Analytics-style metric card: icon + optional badge, large value, label. */
export function MetricTile({
  tone,
  icon: Icon,
  label,
  value,
  badge,
  className,
  compactValue = false,
}: MetricTileProps) {
  const t = metricTile[tone];

  return (
    <div className={cn('p-4', t.surface, className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Icon className={cn('h-4 w-4 shrink-0', t.icon)} />
        {badge ? (
          <Badge variant="outline" className={cn('max-w-[60%] truncate', t.badge)}>
            {badge}
          </Badge>
        ) : null}
      </div>
      <div
        className={cn(
          'font-bold leading-tight break-words',
          compactValue ? 'text-lg' : 'text-2xl',
          t.value
        )}
      >
        {value}
      </div>
      <div className={cn('mt-1 text-sm', t.label)}>{label}</div>
    </div>
  );
}
